import { MediaProportions } from '../models/graphql';
import { FileWithPreview } from '../modules/Publish/MediaLibrary/AddMediaSelectorModal/AddMediaSelectorModal.view';
import { ExternalMedia, SourceType } from '../hooks/useMediaUpload';
import { MD5 } from './md5';

export interface MediaDimensions {
  width: number;
  height: number;
}

export const getMediaFilesize = async (url: string) => {
  const buffer = await getArrayBufferFromUrl(url);
  return buffer.byteLength;
};

export const isMediaIsAnimatedGif = async (url: string) => {
  const buffer = await getArrayBufferFromUrl(url);
  return isGifAnimated(buffer);
};

// Provides the width, height, and type from a URL to an image
export const getImageDimensions = async (url: string): Promise<MediaDimensions> => {
  const img = new Image();
  img.src = url;
  return new Promise(resolve => {
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = img.onabort = () => resolve({ width: 0, height: 0 });
  });
};

const isHtmlVideoElementGuard = (value: EventTarget | null): value is HTMLVideoElement =>
  value instanceof HTMLVideoElement;

export const getVideoDimensions = async (url: string): Promise<MediaDimensions> => {
  return new Promise(resolve => {
    const video = document.createElement('video');
    video.addEventListener(
      'loadedmetadata',
      e => {
        // retrieve dimensions
        const elem = e.currentTarget;
        if (isHtmlVideoElementGuard(elem)) {
          const height = elem.videoHeight;
          const width = elem.videoWidth;
          resolve({ height, width });
        }
        resolve({ height: 0, width: 0 });
      },
      false
    );
    video.src = url;
  });
};

// Provides a MediaProportions enum value based upon the provided width/height
export const getMediaProportion = (width: number, height: number): MediaProportions => {
  if (width === height) {
    return MediaProportions.SQUARE;
  } else if (width > height) {
    return MediaProportions.WIDE;
  } else {
    return MediaProportions.TALL;
  }
};

// Hunts through the buffer to find key indicators to determine if this
// is an animated GIF
export const isGifAnimated = (buffer: ArrayBuffer): boolean => {
  // Set Offsets to be used
  const headerLength = 6;
  const LogicalScreenDescriptionLength = 7;

  // If our buffer is 0, don't process - it's not a GIF
  if (buffer.byteLength === 0) {
    return false;
  }

  // Check GIF Header
  const header = new TextDecoder('ascii').decode(buffer.slice(0, headerLength));
  if (header !== 'GIF89a' && header !== 'GIF87a') {
    return false; // Not a GIF file
  }

  // Start from last 4 bytes of the Logical Screen Descriptor
  const dv = new DataView(buffer, headerLength + LogicalScreenDescriptionLength - 3);
  const globalColorTable = dv.getUint8(0); // aka packet byte
  let globalColorTableSize = 0;

  // check first bit, if 0, then we don't have a Global Color Table
  if (globalColorTable & 0x80) {
    // grab the last 3 bits, to calculate the global color table size -> RGB * 2^(N+1)
    // N is the value in the last 3 bits.
    globalColorTableSize = 3 * Math.pow(2, (globalColorTable & 0x7) + 1);
  }

  // move on to the Graphics Control Extension
  const offset = 3 + globalColorTableSize;

  const extensionIntroducer = dv.getUint8(offset);
  const graphicsControlLabel = dv.getUint8(offset + 1);
  let delayTime = 0;

  // Graphics Control Extension section is where GIF animation data is stored
  // First 2 bytes must be 0x21 and 0xF9
  if (extensionIntroducer & 0x21 && graphicsControlLabel & 0xf9) {
    // skip to the 2 bytes with the delay time
    delayTime = dv.getUint16(offset + 4);
  }

  return !!delayTime;
};

// Checks for support for fetch() prior to using and will default to axios if it does not. Especially true for Explorer users.
export const getArrayBufferFromUrl = (url: string): Promise<ArrayBuffer> => {
  return fetch(url, { mode: 'no-cors' })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}: ${response.statusText}`);
      }

      return response.arrayBuffer();
    })
    .catch(error => {
      if (error.name === 'TypeError') {
        throw new Error('Network error or CORS issue');
      } else {
        throw new Error(`Fetching data from the URL failed: ${error.message}`);
      }
    });
};

// Gets the ArrayBuffer of a file
export const getArrayBufferFromFile = async (file: FileWithPreview): Promise<ArrayBuffer> => {
  return file.arrayBuffer();
};

// Creates an ID for uploading a file or external media item. This ID will be the same across multiple calls.
export const getUploadIdFromFileOrUrl = (fileOrUrl: File | FileWithPreview | ExternalMedia | string): string => {
  // Simple URL.
  if (typeof fileOrUrl === 'string') {
    return MD5(fileOrUrl);
  }

  // File type.
  if (!('sourceType' in fileOrUrl) || fileOrUrl.sourceType === SourceType.file) {
    return MD5(fileOrUrl.name + fileOrUrl.size);
  }

  // External media.
  return MD5(fileOrUrl.url);
};
